import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { jsonLdScriptProps } from "react-schemaorg";

import type { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";

import CustomCard from "components/CustomCard";
import EntityCard from "components/EntityCard";
import EntitySnapshotWidget from "components/EntitySnapshotWidget";
import PaneLayout from "components/PaneLayout";
import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import useActivityTracker from "hooks/useActivityTracker";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { jsonLDForEntity } from "utils/jsonLD";

import EntityPageLayoutMobile from "./EntityPageLayoutMobile";
import EntityPageTabs from "./EntityPageTabs";
import styles from "./style.module.scss";

const EntitySmartScore = dynamic(() => import("components/EntitySmartScore"));

const EntityRecentEvent = dynamic(() => import("../EntityRecentEvent"), {
  ssr: false,
});
const EntityRecentEarnings = dynamic(() => import("./EarningsAlertWidget"), {
  ssr: false,
});
const EntityPageInvestorRelations = dynamic(
  () => import("./EntityPageInvestorRelations"),
  { ssr: false }
);
const EntityPageRelatedIP = dynamic(() => import("./EntityPageRelatedIP"), {
  ssr: false,
});
const DianomiAd = dynamic(() => import("components/DianomiAd"), {
  ssr: false,
});

const EntityPagePeersWidget = dynamic(() => import("./EntityPagePeersWidget"), {
  ssr: false,
});

const ToolTaiwanMonthlyWidget = dynamic(
  () => import("../ToolTaiwanMonthlyScreener/widget"),
  {
    ssr: false,
  }
);

export const SEO_SG_SLUGS = [
  "oversea-chinese-banking-corp",
  "singapore-airlines-ltd",
  "singapore-telecommunications",
  "united-overseas-bank-ltd",
  "dbs-group-holdings-ltd",
  "singapore-exchange-ltd",
  "sats-ltd",
  "capitaland-ltd",
  "aem-holdings-ltd",
  "ifast-corp-ltd",
];

type EntityPageLayoutProps = {
  slug: string;
  children: React.ReactNode;
  hideRightPane?: boolean;
};

export const entityUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}`);
  url.searchParams.append(
    "fields[entities]",
    "slug,short-name,pretty-name,country,sector,entity-description,isin,unique-ticker,yahoo-ticker,total-bullish,total-bearish,security,logo-url" +
      ",smart-score,total-views,total-discussions,market-status"
  );
  return url.href;
};

const EntityPageLayout = ({
  slug,
  children,
  hideRightPane: hideRightPaneProp,
}: EntityPageLayoutProps) => {
  const { setShowBackButton } = useContext(TopNavBarContext);

  const media = useMediaQuery();
  const router = useRouter();

  const hideRightPane = hideRightPaneProp || router.query.tab === "estimates";

  const { trackActivity } = useActivityTracker();

  const { account: loggedInAccount } = useAuthentication();

  const [signupSource, setSignupSource] = useGlobalState("signupSource");

  const { data: entity } = useApi(entityUrl(slug), baseFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Entity) => {
      setSignupSource({ type: "Entity", id: data.id });
    },
  });
  const { data: countries } = useApi(
    entity?.country ? `/api/v2/countries?filter[name]=${entity.country}` : null,
    baseFetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );
  const { data: sectors } = useApi(
    entity?.sector ? `/api/v2/sectors?filter[name]=${entity.sector}` : null,
    baseFetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const country = countries?.[0];
  const sector = sectors?.[0];

  const coverImageUrl = entity?.logoUrl?.xlarge;

  const canonicalUrl =
    emberAppBaseUrl + (router.asPath === "/" ? "" : router.asPath);

  useEffect(() => {
    if (router.route.endsWith("analytics") || router.route.endsWith("more")) {
      if (media.mobile) {
        setShowBackButton(true);
        return;
      }
    }

    setShowBackButton(false);
  }, [media.mobile, router.route, setShowBackButton]);

  useEffect(() => {
    if (entity && entity.slug !== slug) {
      router.replace(router.asPath.replace(`/${slug}/`, `/${entity.slug}/`));
    }
  }, [entity, router, slug]);

  useEffect(() => {
    if (
      entity &&
      signupSource?.id !== entity?.id &&
      location.pathname.startsWith("/entities")
    ) {
      setSignupSource({
        type: "Entity",
        id: entity?.id,
      });
    }
  }, [entity, setSignupSource, signupSource?.id]);

  const investorRelationsRef = useRef<HTMLDivElement>(null);
  const showInvestorRelations = useIntersectionObserver(investorRelationsRef, {
    freezeOnceVisible: true,
  });

  const relatedIPsRef = useRef<HTMLDivElement>(null);
  const showRelatedIPs = useIntersectionObserver(relatedIPsRef, {
    freezeOnceVisible: true,
  });

  const smartPeersRef = useRef<HTMLDivElement>(null);
  const showsmartPeers = useIntersectionObserver(smartPeersRef, {
    freezeOnceVisible: true,
  });

  const [snapshotEmpty, setSnapshotEmpty] = useState(false);

  const [showSmartscore, toggleSmartscore] = useState(false);
  const [smartScoreEmpty, setSmartScoreEmpty] = useState(false);

  const toggle = useMemo(() => {
    if (snapshotEmpty || smartScoreEmpty || router.route.endsWith("/analytics"))
      return <></>;

    return (
      <div className="pb-[10px]">
        <div
          className={twMerge(
            "rounded-[6px]",
            "bg-[#eee]",
            "flex",
            "text-center",
            "text-[#1f1f20]",
            "text-[14px]",
            "p-[3px]"
          )}
        >
          <div
            className={twMerge(
              "flex-1",
              "cursor-pointer",
              "font-medium",
              !showSmartscore ? "bg-white rounded-[4px] shadow" : ""
            )}
            onClick={() => toggleSmartscore(false)}
          >
            Snapshot
          </div>
          <div
            className={twMerge(
              "flex-1",
              "cursor-pointer",
              "font-medium",
              showSmartscore ? "bg-white rounded-[4px] shadow" : ""
            )}
            onClick={() => {
              toggleSmartscore(true);
              trackActivity("smartscore.view", entity);
            }}
          >
            Smartkarma SmartScore
          </div>
        </div>
      </div>
    );
  }, [
    entity,
    router.route,
    showSmartscore,
    smartScoreEmpty,
    snapshotEmpty,
    trackActivity,
  ]);

  const snapshotWidget = (
    <EntitySnapshotWidget
      entity={entity}
      containerClassName={twMerge(showSmartscore && "hidden")}
      customHeader={media.mobile && toggle}
      onEmpty={() => {
        setSnapshotEmpty(true);
        toggleSmartscore(true);
      }}
    />
  );

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />

        {/* Open graph tags for social media etc */}
        <meta property="og:image" content={coverImageUrl} />
        <meta property="og:image:width" content="200" />
        <meta property="og:image:height" content="200" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        {entity && (
          <script
            {...jsonLdScriptProps(
              jsonLDForEntity({ entity, country, sector }) as any
            )}
          ></script>
        )}
      </Head>
      {media.mobile ? (
        <EntityPageLayoutMobile
          entity={entity}
          slug={slug}
          hideSmartScore={
            (!showSmartscore && !router.route.endsWith("/analytics")) ||
            (snapshotEmpty && smartScoreEmpty)
          }
          showPriceChart={entity?.showPriceChart}
          setSmartScoreEmpty={setSmartScoreEmpty}
          toggle={toggle}
          snapshotWidget={snapshotWidget}
        >
          {children}
        </EntityPageLayoutMobile>
      ) : (
        <PaneLayout
          className={styles.entityPageLayout}
          topPaneClassName={"z-[50] relative"}
          top={
            <CustomCard additionalStyle={{ marginBottom: "15px", padding: 0 }}>
              {entity && <EntityCard {...{ entity }} style="v2" />}
            </CustomCard>
          }
          left={<EntityPageTabs slug={slug} entity={entity} />}
          right={
            <div className={styles.entityCards}>
              <DianomiAd type="right_sidebar" />
              {!router.route.endsWith("/analytics") &&
                entity?.showPriceChart &&
                snapshotWidget}
              {entity && !loggedInAccount?.isCorporateOfficer && (
                <EntitySmartScore entity={entity} widget />
              )}
              {loggedInAccount && !loggedInAccount.isPreviewPass && (
                <ToolTaiwanMonthlyWidget entity={entity} />
              )}
              <EntityRecentEarnings />
              <EntityRecentEvent widget />
              <div ref={investorRelationsRef}>
                {showInvestorRelations?.isIntersecting && (
                  <EntityPageInvestorRelations widget />
                )}
              </div>
              <div ref={relatedIPsRef}>
                {showRelatedIPs?.isIntersecting && (
                  <EntityPageRelatedIP widget />
                )}
              </div>
              {!loggedInAccount?.isCorporateOfficer && (
                <div ref={smartPeersRef}>
                  {showsmartPeers?.isIntersecting && <EntityPagePeersWidget />}
                </div>
              )}
            </div>
          }
          hideRight={hideRightPane}
          hideActionBar={hideRightPane}
        >
          <div
            className={twMerge(
              styles.entityDetails,
              hideRightPane ? "!pr-[20px]" : ""
            )}
          >
            {children}
          </div>
        </PaneLayout>
      )}
    </>
  );
};

export default EntityPageLayout;
